import Analytics, { AnalyticsInstance } from 'analytics';

import {
  AnalyticsMediatorPlugin,
  ProductViewedPayload,
  ProductAddedPayload,
  PageViewPayload,
} from './plugins/types';
import { AnalyticsMediatorConfig, Event } from './types';
import {
  ConsentCategory,
  ConsentEvent,
  ConsentMediator,
  ConsentState,
  ConsentType,
  Environment,
} from '../consent';
import { LoggerSkeleton } from '../types';

class AnalyticsMediator {
  #analytics: AnalyticsInstance;
  #consentMediator: ConsentMediator;
  #isInitialized = false;
  #plugins: AnalyticsMediatorPlugin[] = [];
  #logger: LoggerSkeleton;
  #queue: Event[] = [];
  #pluginsEnabledMap: Record<string, boolean> = {};
  #pluginsAreInitialized = false;

  constructor(config: AnalyticsMediatorConfig) {
    this.#consentMediator = config.consentMediator;
    this.#plugins = config?.plugins || [];
    this.#logger = config?.logger || {};

    const isProduction = config?.shopConfig?.environment === Environment.PRODUCTION;

    this.#analytics = Analytics({
      app: 'AnalyticsMediator',
      plugins: this.#plugins,
      debug: !isProduction,
    });
  }

  initialize() {
    if (!this.#isInitialized) {
      this._setPluginConsent();
      this._setPluginAnalyticsListeners();

      try {
        this.#consentMediator.subscribe(
          ConsentEvent.CONSENT_STATE_CHANGE,
          this._onConsentStateChange.bind(this)
        );
        this.#consentMediator.initialize();
      } catch (e: unknown) {
        this.#isInitialized = false;

        this.#logger.error('class=AnalyticsMediator | #initialize', {}, e);
      }
      this.#isInitialized = true;
    }
  }

  private _setPluginConsent() {
    this.#plugins.forEach(plugin => {
      try {
        if (!Object.prototype.hasOwnProperty.call(plugin, 'requiredConsentCategory')) {
          throw new Error('requiredConsentCategory not found');
        }

        this.#logger.debug(
          'class=AnalyticsMediator | #_setPluginConsent | requiredConsentCategory found',
          {
            plugin,
          }
        );
        // Disable all plug-ins by default until consent can be determined
        plugin.enabled = plugin.requiredConsentCategory === ConsentCategory.STRICTLY_NECESSARY;
        this.#pluginsEnabledMap[plugin.name] = plugin.enabled;
      } catch (e) {
        this.#logger.error(
          'class=AnalyticsMediator | #_setPluginConsent | no requiredConsentCategory found',
          {
            plugin,
          },
          e
        );
      }
    });
  }

  private _onConsentStateChange(consentState: ConsentState) {
    this.#logger.debug('class=AnalyticsMediator | #_onConsentStateChange', {
      consentState,
      plugins: this.#plugins,
    });

    this.#plugins.forEach(plugin => {
      if (
        consentState[plugin.requiredConsentCategory as keyof typeof ConsentCategory] ==
        ConsentType.CONSENTED
      ) {
        this.#analytics.plugins.enable(plugin.name);
      } else {
        this.#analytics.plugins.disable(plugin.name, () => {
          this.#logger.debug('class=AnalyticsMediator | #_onConsentStateChange | plugin disabled', {
            plugin,
          });
        });
      }
    });
    this.dequeue();
  }

  private _setPluginAnalyticsListeners() {
    this.#logger.debug('class=AnalyticsMediator | #_setPluginAnalyticsListeners');
    // Events can be seen here:
    // https://github.com/DavidWells/analytics/blob/master/packages/analytics-core/src/events.js
    // https://getanalytics.io/using-listeners/
    const listeners = new Map([
      ['enablePlugin', this.onEnablePlugin],
      ['disablePlugin', this.onDisablePlugin],
      ['ready', this.onReady],
    ]);

    for (const [listenerName, listenerFunction] of listeners) {
      this.#analytics.on(listenerName, listenerFunction.bind(this));
    }
  }

  public page(payload: PageViewPayload): void {
    this.#logger.info('class=AnalyticsMediator | #page', {
      payload,
    });
    // using our own event queueing methods as opposed to this.#analytics.page
    // allows consent management and queuing at cost of lifecycle events
    this.evokeEvent('page', payload);
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  public track(
    eventName: string,
    payload?: any,
    options?: any,
    callback?: ((...params: any[]) => any) | undefined
  ): Promise<any> {
    this.#logger.info('class=AnalyticsMediator | #track', {
      eventName,
      payload,
    });
    return this.#analytics.track(eventName, payload, options, callback);
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */

  public productViewed(payload: ProductViewedPayload): void {
    this.#logger.info('class=AnalyticsMediator | #productViewed | event called ', { payload });
    this.evokeEvent('productViewed', payload);
  }

  public productAdded(payload: ProductAddedPayload): void {
    this.#logger.info('class=AnalyticsMediator | #productAdded | event called ', { payload });
    this.evokeEvent('productAdded', payload);
  }

  public productRemoved(payload: ProductViewedPayload): void {
    this.#logger.info('class=AnalyticsMediator | #productRemoved | event called ', { payload });
    this.evokeEvent('productRemoved', payload);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private evokeEvent(eventName: string, payload: any) {
    // #analytics is a Analytics instance, can't override it's internal typing to incclude custom plugins
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(this.#analytics.plugins).forEach(([pluginName, plugin]: [string, any]) => {
      if (
        Object.prototype.hasOwnProperty.call(plugin, eventName) &&
        typeof plugin[eventName] === 'function'
      ) {
        if (this.#pluginsAreInitialized && this.#pluginsEnabledMap[pluginName]) {
          plugin[eventName]({ ...payload });
        } else {
          this.#queue.push({ pluginName, plugin, payload, eventName });
        }
      }
    });
  }

  private dequeue() {
    if (this.#pluginsAreInitialized) {
      this.#logger.debug('class=AnalyticsMediator | #dequeue | dequeueing events', {
        queue: this.#queue,
      });
      const eventsToSet: Event[] = [];
      this.#queue.forEach((event: Event) => {
        if (this.#pluginsEnabledMap[event.pluginName]) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const analyticsMethod: any = event.plugin[event.eventName];
          if (typeof analyticsMethod === 'function') {
            analyticsMethod({ ...event.payload });
          } else {
            this.#logger.debug('class=AnalyticsMediator | #dequeue | method not found in plugin', {
              eventName: event.eventName,
              plugin: event.pluginName,
            });
          }
        } else {
          eventsToSet.push(event);
        }
      });
      this.#queue = eventsToSet;
    } else {
      this.#logger.debug('class=AnalyticsMediator | #dequeue | plugins unitialized');
    }
  }

  // this 'any' is a payload sent by the Analytics lib
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEnablePlugin({ payload, plugins }: { payload: any; plugins: [AnalyticsMediatorPlugin] }): void {
    this.#logger.debug('class=AnalyticsMediator | #onEnablePlugin', {
      payload,
      plugins,
    });
    payload.plugins.forEach((pluginName: string) => (this.#pluginsEnabledMap[pluginName] = true));
  }

  onReady(): void {
    this.#logger.debug('class=AnalyticsMediator | #onReady');
    this.#pluginsAreInitialized = true;
    this.dequeue();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onDisablePlugin({ payload, plugins }: any): void {
    this.#logger.debug('class=AnalyticsMediator | #onDisablePlugin', {
      payload,
      plugins,
    });
    payload.plugins.forEach((pluginName: string) => (this.#pluginsEnabledMap[pluginName] = false));
  }
}

export default AnalyticsMediator;
